import type {
  Feature,
  FeatureCollection,
  Geometry,
  LineString,
  MultiLineString,
  MultiPolygon,
  Polygon,
  Position,
} from 'geojson'

export const toLonLat = (position: Position): [number, number] | null => {
  if (!Array.isArray(position) || position.length < 2) return null

  const longitude = Number(position[0])
  const latitude = Number(position[1])

  if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return null

  return [longitude, latitude]
}

export const sanitizeLineStringCoords = (coordinates: Position[]): [number, number][] => {
  return coordinates.map(toLonLat).filter((coord): coord is [number, number] => coord !== null)
}

const areCoordinatesEqual = (a: [number, number], b: [number, number]): boolean =>
  a[0] === b[0] && a[1] === b[1]

const sanitizeLinearRing = (ring: Position[]): [number, number][] => {
  const coords = sanitizeLineStringCoords(ring)
  if (coords.length < 3) return []

  const firstCoord = coords[0]
  const lastCoord = coords[coords.length - 1]
  const closedCoords = areCoordinatesEqual(firstCoord, lastCoord) ? coords : [...coords, firstCoord]

  return closedCoords.length >= 4 ? closedCoords : []
}

export const sanitizePolygonRings = (rings: Position[][]): [number, number][][] => {
  return rings.map((ring) => sanitizeLinearRing(ring)).filter((ringCoords) => ringCoords.length >= 4)
}

export const sanitizeGeometry = (geometry: Geometry): Geometry | null => {
  switch (geometry.type) {
    case 'LineString': {
      const coords = sanitizeLineStringCoords((geometry as LineString).coordinates)
      if (coords.length < 2) return null

      return { type: 'LineString', coordinates: coords } as LineString
    }

    case 'Polygon': {
      const rings = sanitizePolygonRings((geometry as Polygon).coordinates)
      if (!rings.length) return null

      return { type: 'Polygon', coordinates: rings } as Polygon
    }

    default:
      return geometry
  }
}

export const sanitizeAndExpandFeature = (feature: Feature): Feature[] => {
  const geometry = feature.geometry
  if (!geometry) return []

  if (geometry.type === 'MultiLineString') {
    const lines = (geometry as MultiLineString).coordinates
      .map((line) => sanitizeLineStringCoords(line))
      .filter((line) => line.length >= 2)

    return lines.map((coords) => ({
      ...feature,
      geometry: { type: 'LineString', coordinates: coords } as LineString,
    }))
  }

  if (geometry.type === 'MultiPolygon') {
    const polygons = (geometry as MultiPolygon).coordinates
      .map((polygon) => sanitizePolygonRings(polygon))
      .filter((poly) => poly.length > 0)

    return polygons.map((coords) => ({
      ...feature,
      geometry: { type: 'Polygon', coordinates: coords } as Polygon,
    }))
  }

  const cleanGeometry = sanitizeGeometry(geometry)
  if (!cleanGeometry) return []

  return [
    {
      ...feature,
      geometry: cleanGeometry,
    },
  ]
}

export const mergeFeatureCollections = (
  collections: FeatureCollection[] | null | undefined,
): FeatureCollection | null => {
  if (!collections || !collections.length) return null

  const features = collections
    .flatMap((collection) => collection?.features ?? [])
    .flatMap((feature) => sanitizeAndExpandFeature(feature))

  if (!features.length) return null

  return {
    type: 'FeatureCollection',
    features,
  }
}

export const getFeatureCoordinates = (feature: Feature): [number, number][] => {
  const geometry = feature.geometry
  if (!geometry) return []

  switch (geometry.type) {
    case 'Point': {
      const coord = toLonLat(geometry.coordinates as Position)
      return coord ? [coord] : []
    }

    case 'LineString': {
      return sanitizeLineStringCoords(geometry.coordinates as Position[])
    }

    case 'Polygon': {
      const rings = sanitizePolygonRings(geometry.coordinates as Position[][])
      return rings.flat()
    }

    default:
      return []
  }
}

export const haversineMeters = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371000
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}
